<script setup lang="ts">
import InterviewDialog from '../InterviewDialog.vue';
import InterviewFigure from '../InterviewFigure.vue';
import InterviewImpression from '../InterviewImpression.vue';
import IntervieweeSummary from '../IntervieweeSummary.vue';
import IconPlanner from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/InterviewTemplate/icon_planner.png/icon_planner.webp';
import { mobileMaxWidth } from 'theme/assets/styles/export.module.scss';

const props = defineProps<{ interviewee: Interviewee }>();

const PlannerDialog: Component<unknown> = (_: unknown | null, { slots }) =>
  h(
    InterviewDialog,
    { variant: 'planner', avatarUrl: IconPlanner },
    {
      default: (props: { clsNotice: string; clsEm: string }) =>
        slots.default?.(props),
    }
  );

const UserDialog: Component<unknown> = (_: unknown, { slots }) =>
  h(
    InterviewDialog,
    { variant: 'user', avatarUrl: props.interviewee.avatarUrl },
    {
      default: (props: { clsNotice: string; clsEm: string }) =>
        slots.default?.(props),
    }
  );
</script>

<template>
  <div :class="$style.container">
    <IntervieweeSummary
      :class="$style.container__summary"
      :interviewee="interviewee"
    />
    <PlannerDialog>
      Yさん、こんにちは！<br />
      本日は事前に伺っているご要望を踏まえて、低リスク資産を中心に、実際の資産配分についてお話をさせていただければと思います。内容に違和感はございますか？
    </PlannerDialog>

    <UserDialog> いいえ大丈夫です。よろしくお願いいたします！ </UserDialog>

    <InterviewFigure :class="$style.container__figures" caption="株式と債権">
      <div :class="$style.figure">
        <picture>
          <source
            srcset="./fig_2_1__sp.svg"
            :media="`(max-width: ${mobileMaxWidth})`"
          />
          <img :class="$style.figure__img" src="./fig_2_1.svg" />
        </picture>
      </div>
      <div :class="$style.figure">
        <picture>
          <source
            srcset="./fig_2_2__sp.svg"
            :media="`(max-width: ${mobileMaxWidth})`"
          />
          <img :class="$style.figure__img" src="./fig_2_2.svg" />
        </picture>
      </div>
    </InterviewFigure>

    <PlannerDialog v-slot="{ clsEm }">
      <span :class="clsEm">資産運用を大きく分けると、株式なのか債券なのか</span
      >というお話になります。もちろんこの２つ以外にも不動産など、いろいろな商品がありますが、まずはこの2つについてお考えになると良いと思います。
    </PlannerDialog>

    <PlannerDialog v-slot="{ clsEm }">
      株式はすでにお持ちなので、イメージしやすいかと存じますが、一般的に長期的にはその国の経済がしっかり成長していく限りは株価はしっかり上がっていくことが想定されます。ただ、短い期間を切り取りますと、例えばリーマンショックがあった時は短期間で株価が半分以下になってしまったということもありまして、リスクもございます。特に銘柄を絞ったら絞った分だけリスクに晒されるので、<span
        :class="clsEm"
        >分散していくことが非常に重要</span
      >になってきます。なので基本的には<span :class="clsEm"
        >投資信託というパッケージ商品</span
      >をお勧めさせていただいております。
    </PlannerDialog>

    <PlannerDialog>
      続きまして債券のお話ですね。債券はご購入されたことはありますか？
    </PlannerDialog>

    <UserDialog> 日本の国債を買ったことがある気がします。 </UserDialog>

    <PlannerDialog>
      承知しました！ありがとうございます。債券は比較的安全性の高い資産となります。満期まで持っていただくと満期時に元本が返ってきて、さらに利息分の収益が上がる商品です。リスクとしては、発行体が破綻してしまうような場合です。
    </PlannerDialog>

    <PlannerDialog v-slot="{ clsEm, clsNotice }">
      私たちが債券を組み入れる際にお勧めしているのが<span :class="clsEm"
        >米国の債券</span
      >です。実は15年ぶりの高金利水準でして、今非常に高い水準です。債券の魅力の1つとして基本的には買ったタイミングで金利が固定できるところがございまして、<span
        :class="clsEm"
        >高金利水準でご投資いただくことによって高いリターンが期待できます。</span
      ><span :class="clsNotice"
        >※2024年2月時点 ※為替変動リスクがございます</span
      >
    </PlannerDialog>

    <UserDialog>
      なるほど、理解できました。米国債すごく良さそうですね！株を売却した分、全部を債券にするのはいい考えなのでしょうか？
    </UserDialog>

    <PlannerDialog v-slot="{ clsEm }">
      そうですね、どちらかに偏りすぎるというのはあまり良くないので、50%を株式型にもう50%を債券にと<span
        :class="clsEm"
        >バランスよく分散されるのがお勧めです。</span
      >
    </PlannerDialog>

    <UserDialog>
      株式の場合、種類もたくさんあるし、値上がりが気になってしまって少し負担が大きいかもしれないです。
    </UserDialog>

    <PlannerDialog v-slot="{ clsEm }">
      かしこまりました。ではもう少し債券の保有率を高めて…株式をご購入される場合も、<span
        :class="clsEm"
        >オールカントリー（株式型投資信託）</span
      >を中心に置いてご検討されると良いですね。
    </PlannerDialog>

    <InterviewFigure
      :class="$style.container__figures"
      caption="金融商品の一般的な違いと複利運用とは"
    >
      <div :class="$style.figure">
        <picture>
          <source
            srcset="./fig_2_3__sp.svg"
            :media="`(max-width: ${mobileMaxWidth})`"
          />
          <img :class="$style.figure__img" src="./fig_2_3.svg" />
        </picture>
      </div>
      <div :class="$style.figure">
        <picture>
          <source
            srcset="./fig_2_4__sp.svg"
            :media="`(max-width: ${mobileMaxWidth})`"
          />
          <img :class="$style.figure__img" src="./fig_2_4.svg" />
        </picture>
      </div>
    </InterviewFigure>

    <PlannerDialog v-slot="{ clsEm }">
      債券を活用した商品として、<span :class="clsEm"
        >個別債券、投資信託、生命保険</span
      >の3つが挙げられます。先に述べた金利の固定ですが、投資信託の場合はできなくなります。また、複利での運用や税金の取り扱い、中途解約ができるのかに違いがございます。
    </PlannerDialog>

    <UserDialog> 保険でも債券の運用ができるのですね！ </UserDialog>

    <PlannerDialog>
      はい、今回のYさんの場合、いくつかの商品を比べましたが、私たちとしては保険の商品をご提案させていただきます。<br />購入は次の3つのリスクを踏まえた上でご決定くださいね。1つ目は為替のリスクです。円安でしたらもちろんその分資産が増えますが、今回の商品の場合99円くらいまで円高になりますと、損失が出てしまいます。2つ目は中途解約のリスクです。満期を待たずにご解約される場合に手数料がかかってしまいます。必ずしもマイナスとは言い切れないのですが、リスクになってしまいます。3つ目は保険会社が破綻してしまうリスクです。万が一そういったことがあっても契約者の方の保護の措置が図られる仕組みにはなっているのですが、場合によっては保険額が削減されてしまう恐れがございます。
    </PlannerDialog>

    <UserDialog> 承知しました！その点を含めて妻と相談してみます。 </UserDialog>

    <PlannerDialog>
      ご検討よろしくお願いいたします。では今回の面談を振り返りますね。
    </PlannerDialog>

    <InterviewFigure
      :class="$style.container__figures"
      caption="Yさんの面談振り返り"
    >
      <div :class="$style.figures__col">
        <div :class="$style.figure">
          <div :class="$style.listing">
            <div :class="$style.listing__caption">実践したこと</div>
            <ul :class="$style.listing__listing">
              <li>資産運用における株式と債券のご説明</li>
              <li>債券を取り扱う商品ごとの違いの説明と具体的な商品のご提案</li>
            </ul>
          </div>
        </div>

        <div :class="$style.figure">
          <div :class="$style.listing">
            <div :class="$style.listing__caption">面談を通じて分かったこと</div>
            <ul :class="$style.listing__listing">
              <li>
                <span :class="$style.listing__em"
                  >利率の水準が高く、安全性が株式よりも高い米国債券</span
                >に魅力を感じる
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div :class="[$style.figure, $style['figure--withNote']]">
        <div :class="[$style.listing, $style.figure__mainItem]">
          <div :class="$style.listing__caption">課題・改善・実施したい点</div>
          <ul :class="$style.listing__listing">
            <li>
              債券の商品を比較し、Yさんの場合は税金のコストを抑えられる※保険での債券運用をまずは検討する
            </li>
            <li>
              株式も個別銘柄ではなく投資信託のオールカントリーを中心に検討し、<span
                :class="$style.listing__em"
                >資産運用のリスクを下げていく</span
              >
            </li>
          </ul>
        </div>
        <p :class="$style.figure__note">
          ※全ての方が保険の方が税金のコストを抑えられるとは限りません
        </p>
      </div>
    </InterviewFigure>

    <PlannerDialog>
      資産が何かに偏るのはやはりリスクが高いので、まずは利率が高いうちに米国債券に資産を分散されることをご検討いただければと思います。
    </PlannerDialog>

    <UserDialog v-slot="{ clsEm }">
      ありがとうございます！いきなり商品の提案ではなく、<span :class="clsEm"
        >まずはどれを選んだらいいのかを決めるために必要な知識をリスクも含めてしっかりとご説明いただき</span
      >、とても参考になりました。オンラインで完結するのもありがたかったです。しっかり考えて、お返事しますね。
    </UserDialog>

    <PlannerDialog> はい、引き続きよろしくお願いいたします。 </PlannerDialog>

    <InterviewImpression
      :class="$style.container__impression"
      :avatar-url="interviewee.avatarUrl"
      notice="※ この記事は、2024年2月にお客様からご相談いただいた内容について、対象のお客様の許可を得て掲載しています ※本レビューの執筆に際し、謝礼をお渡ししました。この対価はレビューの執筆に対する感謝を表すものであり、レビューの内容や意見に影響を与えるものではありません ※内容は、個々の体験や意見に基づいています。お客様の個人的な意見であり、必ずしも全ての方に当てはまるものではありません"
      body="資産運用や分散投資についてほとんど不勉強な中、基本的な理解を促進いただいた上で、外貨建て保険をご提案いただきました。実際に購入したことにより、資産の分散と、毎年一定金額の利払いを獲得できました。"
    />
  </div>
</template>

<style module lang="scss">
.container {
  display: flex;
  flex-direction: column;
  gap: 24px;

  @include mobile-only {
    gap: 16px;
  }

  &__figures {
    margin: 8px 0;
  }

  &__impression {
    margin-top: 24px;
  }

  &__summary {
    margin-bottom: 24px;
  }
}

.figures {
  &__col {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.figure {
  background-color: $color-surface-primary;
  border-radius: 8px;

  &__mainItem {
    height: 100%;
    flex: 1;
  }

  &__img {
    height: 100%;
    max-height: 240px;
    display: block;
    margin: 0 auto;
    @include mobile-only {
      max-height: unset;
      width: 100%;
    }
  }

  &--withNote {
    display: flex;
    flex-direction: column;
  }

  &__note {
    text-align: end;
    font-size: 10px;
    padding: 4px 24px;
  }
}

.listing {
  line-height: 1.8;
  padding: 16px 24px;
  &__caption {
    font-weight: bold;
  }
  &__listing {
    margin-left: 20px;
    list-style-type: disc;
  }
  &__em {
    font-weight: bold;
    background: linear-gradient(transparent 0% 60%, $color-yellow-300 60%);
    padding-bottom: 2px;
  }
}
</style>
